import React from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';

import { IVirtualizedListConfiguration, IVirtualizedListProps, IVirtualizedListState } from './models/index.js';
import Row from './styledComponents/Row.js';
import Wrapper from './styledComponents/Wrapper.js';
import { VIRTUALIZED_LIST_CATEGORY_KEY, VIRTUALIZED_LIST_COMPONENT_KEY } from './VirtualizedList.meta.js';

export interface IRowItem {
	index: number;
	key: any;
	style: any;
	parent: any;
}

export class VirtualizedList extends ComponentBase<
	IVirtualizedListProps,
	IVirtualizedListState,
	IVirtualizedListConfiguration
> {
	categoryKey = VIRTUALIZED_LIST_CATEGORY_KEY;
	componentKey = VIRTUALIZED_LIST_COMPONENT_KEY;

	constructor(props: IVirtualizedListProps) {
		super(props);

		this.cache = new CellMeasurerCache({
			fixedWidth: true,
			defaultHeight: props.defaultRowHeight || 100,
		});

		makeObservable(this, {
			updated: observable,
			cache: observable,
			items: computed,
			autosizeFn: computed,
		});
	}

	updated = 1; // Setting this forces the autosizer to re-render when the component props change
	cache: CellMeasurerCache;

	get items() {
		return (this.props && this.props.items) || [];
	}

	renderRow = ({ index, key, style, parent }: IRowItem) => {
		return (
			<CellMeasurer key={key} cache={this.cache} parent={parent} columnIndex={0} rowIndex={index}>
				<Row key={key} style={style}>
					{this.items[index]}
				</Row>
			</CellMeasurer>
		);
	};

	get autosizeFn() {
		const { overscanRowCount = 3 } = this.props;
		return ({ width, height }: { width: any; height: any }) => {
			return (
				<List
					updated={this.updated++}
					width={width}
					height={height}
					rowHeight={this.cache.rowHeight}
					rowRenderer={this.renderRow}
					rowCount={this.items.length}
					overscanRowCount={overscanRowCount}
				/>
			);
		};
	}

	render() {
		const { className } = this.props;

		return (
			<Wrapper className={className}>
				<AutoSizer children={this.autosizeFn} />
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(VirtualizedList),
	VIRTUALIZED_LIST_CATEGORY_KEY,
	VIRTUALIZED_LIST_COMPONENT_KEY,
);
